/**
 * ResultCard — generic actionable result card.
 *
 * A reusable container for AI-generated results with:
 *   - Colored header with icon + title
 *   - Free-form content (children)
 *   - Action buttons (primary + secondary)
 *   - Visual variant (formula, analysis, explanation, cleaning, warning, suggestion)
 *   - Optional dismiss button
 *   - Fade-in animation on mount
 *
 * Used by FormulaCard, ExplanationCard, CleaningPreviewCard, AnalysisResults.
 */

import React, { useState, useCallback } from 'react';
import { Button, Text, Tooltip } from '@fluentui/react-components';
import { DismissRegular, CheckmarkRegular } from '@fluentui/react-icons';

// ─── Types ──────────────────────────────────────────────

export type ResultCardVariant =
  | 'formula'
  | 'analysis'
  | 'explanation'
  | 'cleaning'
  | 'warning'
  | 'suggestion'
  | 'default';

export interface ResultCardAction {
  /** Unique key for React list rendering. Falls back to label. */
  readonly key?: string;
  readonly label: string;
  readonly icon?: React.ReactElement;
  readonly onClick: () => void | Promise<void>;
  /** Render as primary (filled) button. */
  readonly primary?: boolean;
  readonly disabled?: boolean;
  /** Tooltip text. */
  readonly tooltip?: string;
  /**
   * If set, the button temporarily shows this label + a checkmark
   * after a successful click (e.g. "Copie !").
   */
  readonly feedbackLabel?: string;
}

export interface ResultCardProps {
  readonly title: string;
  readonly icon?: React.ReactElement;
  readonly variant?: ResultCardVariant;
  readonly actions?: readonly ResultCardAction[];
  /** Extra metadata rendered to the right of the title. */
  readonly metadata?: React.ReactNode;
  readonly children: React.ReactNode;
  /** Show a dismiss (X) button in the header. */
  readonly onDismiss?: () => void;
}

// ─── Component ──────────────────────────────────────────

export const ResultCard: React.FC<ResultCardProps> = ({
  title,
  icon,
  variant = 'default',
  actions,
  metadata,
  children,
  onDismiss,
}) => (
  <div className={`result-card result-card--${variant}`}>
    {/* Header */}
    <div className="result-card-header">
      <div className="result-card-title-row">
        {icon && <span className="result-card-icon">{icon}</span>}
        <Text size={200} weight="semibold" className="result-card-title">
          {title}
        </Text>
        {metadata && <span className="result-card-meta">{metadata}</span>}
      </div>
      {onDismiss && (
        <Button
          size="small"
          appearance="subtle"
          icon={<DismissRegular />}
          onClick={onDismiss}
          className="result-card-dismiss"
        />
      )}
    </div>

    {/* Body */}
    <div className="result-card-body">{children}</div>

    {/* Actions */}
    {actions && actions.length > 0 && (
      <div className="result-card-actions">
        {actions.map((action) => (
          <ActionButton key={action.key ?? action.label} action={action} />
        ))}
      </div>
    )}
  </div>
);

// ─── Action button with feedback ────────────────────────

const ActionButton: React.FC<{ action: ResultCardAction }> = ({ action }) => {
  const [feedback, setFeedback] = useState(false);

  const handleClick = useCallback(async () => {
    try {
      await action.onClick();
      if (action.feedbackLabel) {
        setFeedback(true);
        setTimeout(() => setFeedback(false), 2000);
      }
    } catch {
      // swallow — caller should handle errors
    }
  }, [action]);

  const label = feedback ? action.feedbackLabel! : action.label;
  const icon = feedback ? <CheckmarkRegular /> : action.icon;

  const btn = (
    <Button
      size="small"
      appearance={action.primary ? 'primary' : 'subtle'}
      icon={icon}
      onClick={handleClick}
      disabled={action.disabled || feedback}
    >
      {label}
    </Button>
  );

  return action.tooltip ? (
    <Tooltip content={action.tooltip} relationship="description">
      {btn}
    </Tooltip>
  ) : btn;
};
